import { StrictMode } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import App from './App';
import { AuthProvider } from './contexts/JWTContext';
import { SettingsProvider } from './contexts/SettingsContext';
import { CustomerProvider } from './contexts/CustomerContext';
import { CrmProvider } from './contexts/CrmContext';
import { OrderProvider } from './contexts/OrderContext';
import { GlobalsProvider } from './contexts/GlobalsContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { createRoot } from 'react-dom/client';
import { StyledEngineProvider } from '@mui/material';
import { ThemeProvider } from './contexts/ThemeContext';
import { Logger } from './utils/logger';
import { SnackbarProvider } from './contexts/SnackbarContext';
import { OrderStepsProvider } from './contexts/OrderStepsContext';

const container = document.getElementById('root');
const root = createRoot(container!);
const chunkLoadRetryKey = 'sfa-web-chunk-load-retry';

const isChunkLoadError = (reason: any) => {
  const message = reason?.message ?? String(reason ?? '');

  return (
    message.includes('ChunkLoadError') ||
    message.includes('Loading chunk') ||
    message.includes('Failed to fetch dynamically imported module') ||
    message.includes('Importing a module script failed')
  );
};

window.onerror = function (msg, url, line, col, error?: Error) {
  //Log uncuaght errors that may bubble up.
  Logger.error(msg, url, line, col, error);
};

window.addEventListener('unhandledrejection', (event) => {
  if (!isChunkLoadError(event.reason) || sessionStorage.getItem(chunkLoadRetryKey)) {
    return;
  }

  Logger.error('Chunk load failed, reloading once to refresh cached assets.', event.reason);
  sessionStorage.setItem(chunkLoadRetryKey, 'true');
  window.location.reload();
});

window.setTimeout(() => {
  sessionStorage.removeItem(chunkLoadRetryKey);
}, 30000);

console.log('React App started');

root.render(
  <StrictMode>
    <StyledEngineProvider injectFirst>
      <SnackbarProvider>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <AuthProvider>
            <SettingsProvider>
              <ThemeProvider>
                <BrowserRouter>
                  <CustomerProvider>
                    <GlobalsProvider>
                      <OrderProvider>
                        <NotificationProvider>
                          <CrmProvider>
                            <OrderStepsProvider>
                              <App />
                            </OrderStepsProvider>
                          </CrmProvider>
                        </NotificationProvider>
                      </OrderProvider>
                    </GlobalsProvider>
                  </CustomerProvider>
                </BrowserRouter>
              </ThemeProvider>
            </SettingsProvider>
          </AuthProvider>
        </LocalizationProvider>
      </SnackbarProvider>
    </StyledEngineProvider>
  </StrictMode>
);
